import { twJoin } from "tailwind-merge";
import { v5 } from "uuid";

import type {
  PinModalData,
  PinPreviewData,
} from "src/analytics/dashboard/PinChartModal";
import type { Dashboard } from "src/analytics/dashboard/useDashboard";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { VisualizeMetricsToolMessage } from "src/analytics/metrics/VisualizeMetrics/VisualizeMetricsToolMessage";
import {
  ToolCallGroup,
  type ToolCallGroupItem,
} from "src/base-components/agents/ToolCallGroup";
import { ChatLoadingIndicator } from "src/base-components/Chat/ChatLoadingIndicator";
import { ChatLoadingSpinner } from "src/base-components/Chat/ChatLoadingSpinner";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { ChatUserBubble } from "src/base-components/Chat/ChatUserBubble";
import {
  type AIMessage,
  type HumanMessage,
  type SessionMessagesResponseMessagesInner,
  type ToolCallContentBlock,
  type ToolMessage,
  ToolResultStatus,
  ToolType,
} from "src/clients/agent-management";
import type { ChartDefinition } from "src/clients/analytics-dashboard";
import type { QueryContext } from "src/clients/analytics-query";
import { Callout } from "src/design-system/Callout";

const PIN_ID_NAMESPACE = "1b671a64-40d5-491e-99b0-da01ff1f3341";
const pinIdForToolCall = (toolCallId: string): string =>
  v5(toolCallId, PIN_ID_NAMESPACE);

export const humanMessageText = (message: HumanMessage): string =>
  message.invocation_message ?? "Unexpected missing invocation_message";

// agent-management serves the raw tool name as `display_name`, so the chat
// shows "query_metrics" as the tool-call title. Until the server provides
// human-readable names (OPTI-7866), patch known tools here.
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  query_metrics: "Query",
};

const withReadableToolName = (
  block: ToolCallContentBlock,
): ToolCallContentBlock => {
  const displayName = TOOL_DISPLAY_NAMES[block.name];
  return displayName
    ? { ...block, metadata: { ...block.metadata, display_name: displayName } }
    : block;
};

const buildToolResultMap = (
  messages: SessionMessagesResponseMessagesInner[],
): Map<string, ToolMessage> => {
  const entries = messages
    .filter((m): m is { type: "tool" } & ToolMessage => m.type === "tool")
    .map((m) => [m.tool_call_id, m] as const);
  return new Map(entries);
};

export type VisibleChatMessage =
  | ({ type: "human" } & HumanMessage)
  | ({ type: "ai" } & AIMessage);

// Tool messages are folded into the AI message that called them.
export const visibleChatMessages = (
  messages: SessionMessagesResponseMessagesInner[],
): VisibleChatMessage[] =>
  messages.filter(
    (m): m is VisibleChatMessage => m.type === "human" || m.type === "ai",
  );

export type ChatTurn = {
  key: string;
  messages: VisibleChatMessage[];
};

export const chatTurns = (
  messages: SessionMessagesResponseMessagesInner[],
): ChatTurn[] =>
  visibleChatMessages(messages).reduce<ChatTurn[]>((turns, message, idx) => {
    const openTurn = turns.at(-1);
    if (message.type === "human" || openTurn === undefined) {
      turns.push({
        key: message.id ?? `${message.type}-${idx}`,
        messages: [message],
      });
    } else {
      openTurn.messages.push(message);
    }
    return turns;
  }, []);

const ErrorCallout: React.FC<{ message: string }> = ({ message }) => (
  <li className="flex-shrink-0">
    <Callout dataLoc="copilot-error" variant="error">
      {message}
    </Callout>
  </li>
);

type ChartItem = {
  kind: "chart";
  key: string;
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
};

type TurnItem =
  | { kind: "text"; key: string; text: string }
  | ChartItem
  | {
      kind: "tool-thread";
      key: string;
      items: ToolCallGroupItem[];
      isInProgress: boolean;
    };

/**
 * Flattens a turn's AI messages into one ordered list of text, tool calls
 * (with results) and charts. The structured-output tool is skipped: it
 * carries the final answer, not a tool call the user should see.
 */
const aiFlatItems = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
): (ToolCallGroupItem | ChartItem)[] => {
  const items: (ToolCallGroupItem | ChartItem)[] = [];
  turn.messages
    .filter((m): m is { type: "ai" } & AIMessage => m.type === "ai")
    .forEach((message, msgIdx) => {
      const messageKey = message.id ?? `${turn.key}-ai-${msgIdx}`;
      (message.content_blocks ?? []).forEach((block, blockIdx) => {
        switch (block.type) {
          case "text":
            items.push({
              kind: "text",
              key: `${messageKey}-text-${blockIdx}`,
              text: block.text,
            });
            return;
          case "tool_call": {
            // Skip the structured-output tool (json_response) — it's the
            // agent's final answer, not a user-facing tool call.
            if (block.metadata.type === ToolType.STRUCTURED_OUTPUT) {
              return;
            }
            const toolResult = toolResults.get(block.id) ?? null;
            if (
              block.name === "reply_visualize_metrics" &&
              toolResult?.status !== ToolResultStatus.ERROR
            ) {
              items.push({
                kind: "chart",
                key: `${messageKey}-tool-${block.id}`,
                toolCall: block,
                toolResult,
              });
              return;
            }
            items.push({
              kind: "tool",
              toolCall: withReadableToolName(block),
              toolResult,
            });
            return;
          }
          // Contract variants newer than this bundle's client render nothing.
          default:
            return;
        }
      });
    });
  return items;
};

/**
 * Splits a turn into what the user sees: tool calls, plus any text the agent
 * wrote between its first and last tool call or chart, collapse into one
 * thread, while the opening text, the final answer and charts stay outside.
 * While the turn is still running, trailing text stays in the thread too,
 * since it may be narration for a tool call that has not arrived yet.
 */
const aiTurnItems = (
  turn: ChatTurn,
  toolResults: Map<string, ToolMessage>,
  isInProgress: boolean,
): TurnItem[] => {
  const flat = aiFlatItems(turn, toolResults);
  const firstToolIdx = flat.findIndex((item) => item.kind === "tool");
  const foldEndIdx =
    isInProgress && firstToolIdx !== -1
      ? flat.length
      : flat.findLastIndex((item) => item.kind !== "text");
  const thread: ToolCallGroupItem[] = [];
  return flat.reduce<TurnItem[]>((items, item, idx) => {
    if (item.kind === "chart") {
      items.push(item);
    } else if (
      item.kind === "tool" ||
      (idx > firstToolIdx && idx < foldEndIdx)
    ) {
      thread.push(item);
      if (idx === firstToolIdx) {
        items.push({
          kind: "tool-thread",
          key: `${turn.key}-thread`,
          items: thread,
          isInProgress,
        });
      }
    } else {
      items.push(item);
    }
    return items;
  }, []);
};

export type DashboardPinning = {
  dashboard: Dashboard;
  pinChart: (definition: ChartDefinition, id?: string, title?: string) => void;
  unpinChart: (id: string) => void;
  pinDisabled: boolean;
  pinDisabledReason?: string;
};

export const LiveAnalyticsMessages: React.FC<{
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  isProvisioning: boolean;
  failureMessage: string | null;
  labelDictionaries: LabelDictionaries;
  dataSource?: QueryContext;
  pinning?: DashboardPinning;
  onRequestPin?: (data: PinModalData) => void;
  lastTurnRef?: React.Ref<HTMLLIElement>;
  reservesTurnHeight?: boolean;
}> = ({
  messages,
  isActive,
  isProvisioning,
  failureMessage,
  labelDictionaries,
  dataSource,
  pinning,
  onRequestPin,
  lastTurnRef,
  reservesTurnHeight = false,
}) => {
  const toolResults = buildToolResultMap(messages);

  const sessionSubstatus = isActive && !isProvisioning ? "running" : null;

  const turns = chatTurns(messages);

  const renderItem = (item: TurnItem) => {
    if (item.kind === "text") {
      return (
        <ChatTextContent
          key={item.key}
          content={item.text}
          delay={0}
          useMarkdownRenderer
        />
      );
    }
    if (item.kind === "tool-thread") {
      return (
        <ToolCallGroup
          key={item.key}
          alwaysShowToolDetails
          dataLoc="copilot-tool-thread"
          isInProgress={item.isInProgress}
          sessionSubstatus={sessionSubstatus}
          showDescription={false}
          showTokenUsage={false}
          items={item.items}
        />
      );
    }
    const pinId = pinIdForToolCall(item.toolCall.id);
    const isPinned = pinning != null && pinId in pinning.dashboard.pins;

    const handleRequestPin =
      pinning && !isPinned && onRequestPin
        ? (sql: string, preview: PinPreviewData, parameterNames: string[]) =>
            onRequestPin({
              ...preview,
              sql,
              pinId,
              parameterNames,
            })
        : undefined;

    return (
      <VisualizeMetricsToolMessage
        key={item.key}
        dataSource={dataSource}
        labelDictionaries={labelDictionaries}
        onRequestPin={handleRequestPin}
        onUnpin={
          pinning && isPinned ? () => pinning.unpinChart(pinId) : undefined
        }
        pinDisabled={pinning?.pinDisabled}
        pinDisabledReason={pinning?.pinDisabledReason}
        toolCall={item.toolCall}
        toolResult={item.toolResult}
      />
    );
  };

  const renderStatus = (isRunningTools: boolean) => (
    <>
      {isActive &&
        !isRunningTools &&
        (isProvisioning ? (
          <ChatLoadingSpinner
            dataLoc="copilot-provisioning"
            text="Setting up agent..."
          />
        ) : (
          <ChatLoadingIndicator mode="loading" dataLoc="copilot-thinking" />
        ))}
      {failureMessage && <ErrorCallout message={failureMessage} />}
    </>
  );

  return (
    <>
      {turns.map((turn, turnIdx) => {
        const isLastTurn = turnIdx === turns.length - 1;
        const humanMessage = turn.messages.find(
          (m): m is { type: "human" } & HumanMessage => m.type === "human",
        );
        const aiItems = aiTurnItems(turn, toolResults, isLastTurn && isActive);
        const isRunningTools =
          isActive &&
          aiItems.some(
            (item) =>
              item.kind === "tool-thread" &&
              item.items.some(
                (entry) => entry.kind === "tool" && entry.toolResult == null,
              ),
          );
        return (
          <li
            key={turn.key}
            ref={isLastTurn ? lastTurnRef : undefined}
            className={twJoin(
              // scroll-mt-2 matches the list's gap-y-2, so the gap above a
              // pinned turn cannot show the previous answer.
              "flex shrink-0 scroll-mt-2 flex-col",
              isLastTurn && reservesTurnHeight && "min-h-full",
            )}
            data-loc="copilot-turn"
          >
            <ul className="flex flex-col gap-y-2">
              {humanMessage && (
                <ChatMessage dataLoc="copilot-message-user" role="user">
                  <ChatUserBubble message={humanMessageText(humanMessage)} />
                </ChatMessage>
              )}
              {aiItems.length > 0 && (
                <ChatMessage dataLoc="copilot-message-ai" role="assistant">
                  <div className="flex flex-col gap-2">
                    {aiItems.map(renderItem)}
                  </div>
                </ChatMessage>
              )}
              {isLastTurn && renderStatus(isRunningTools)}
            </ul>
          </li>
        );
      })}
      {turns.length === 0 && renderStatus(false)}
    </>
  );
};
